/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';

import Modal from '../../../../components/Modal/Modal';

// =============================================================================
// ServiceTypeFormModal — criar/editar um serviço do catálogo unificado
// (handoff 0039 §2). Dois donos por permissão, dois endpoints:
//   - LOGÍSTICA (canManage): code (imutável após criar) + descrição + tipo de
//     produto + categoria + tempo de execução + ativo.
//   - FINANCEIRO (canManageCommission): só commission_value_per_unit (PATCH
//     /:id/commission dedicado).
//
// A ficha mostra todos os campos, mas só habilita os que o operador pode editar.
// Na criação (só logística) a comissão NÃO é definida aqui — nasce 0,00 e o
// financeiro preenche depois. O salvamento devolve { catalogBody?, commission? }
// e o pai dispara as chamadas certas. 409 (code duplicado) e 422 (errors[]) são
// mapeados aqui.
// =============================================================================

// MAIÚSCULAS/dígitos/underscore, começando por letra (ex.: TEFN, HIGP).
const CODE_RE = /^[A-Z][A-Z0-9_]*$/;

const Body = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
`;

const WarnBox = styled.div`
   background: rgba(211, 47, 47, 0.08);
   border: 1px solid rgba(211, 47, 47, 0.4);
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 0.8rem;
   color: #b71c1c;
   line-height: 1.5;
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;

   label {
      font-size: 0.75rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   }

   input {
      padding: 8px 10px;
      font-size: 0.85rem;
      border-radius: 8px;
      border: 1px solid
         ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
      font-family: inherit;
   }

   input:disabled {
      background: rgba(0, 0, 0, 0.04);
      cursor: not-allowed;
   }
`;

const TwoCol = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 12px;

   @media (max-width: 480px) {
      grid-template-columns: 1fr;
   }
`;

const Hint = styled.span`
   font-size: 0.7rem;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#888'};
`;

const CheckboxRow = styled.label`
   display: inline-flex;
   align-items: center;
   gap: 8px;
   font-size: 0.85rem;
   cursor: pointer;
   user-select: none;
`;

const FieldError = styled.span`
   font-size: 0.7rem;
   color: #c62828;
`;

function mapErrors(error) {
   const errors = error?.response?.data?.errors;
   if (!Array.isArray(errors)) return {};
   const result = {};
   errors.forEach((entry) => {
      if (entry?.field) result[entry.field] = entry.message;
   });
   return result;
}

export default function ServiceTypeFormModal({
   mode,
   serviceType = null,
   canManage = false,
   canManageCommission = false,
   onClose,
   onSubmit,
}) {
   const isEdit = mode === 'edit';

   const [code, setCode] = useState('');
   const [description, setDescription] = useState('');
   const [productType, setProductType] = useState('');
   const [serviceCategory, setServiceCategory] = useState('');
   const [duration, setDuration] = useState('');
   const [isActive, setIsActive] = useState(true);
   const [commission, setCommission] = useState('');
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [fieldErrors, setFieldErrors] = useState({});
   const [generalError, setGeneralError] = useState(null);

   useEffect(() => {
      setCode(serviceType?.service_code ?? '');
      setDescription(serviceType?.service_description ?? '');
      setProductType(serviceType?.product_type ?? '');
      setServiceCategory(serviceType?.service_category ?? '');
      setDuration(
         serviceType?.estimated_completion_service_time != null
            ? String(serviceType.estimated_completion_service_time)
            : ''
      );
      setIsActive(
         serviceType?.is_active != null ? Boolean(serviceType.is_active) : true
      );
      setCommission(
         serviceType?.commission_value_per_unit != null
            ? String(serviceType.commission_value_per_unit)
            : ''
      );
      setFieldErrors({});
      setGeneralError(null);
      setIsSubmitting(false);
   }, [serviceType]);

   // Comissão original (string normalizada) — usada para só disparar o PATCH
   // de comissão quando o valor realmente muda.
   const originalCommission =
      serviceType?.commission_value_per_unit != null
         ? String(serviceType.commission_value_per_unit)
         : '';

   const handleConfirm = async () => {
      const errors = {};

      // ─── Validação dos campos da logística (só se ela edita) ───
      if (canManage) {
         if (!isEdit) {
            if (!code.trim()) {
               errors.service_code = 'Informe o código.';
            } else if (!CODE_RE.test(code.trim())) {
               errors.service_code =
                  'Use MAIÚSCULAS, dígitos e _ , começando por letra.';
            }
         }
         if (!description.trim()) {
            errors.service_description = 'Informe a descrição.';
         }
         if (!productType.trim()) {
            errors.product_type = 'Informe o tipo de produto.';
         }
         if (!serviceCategory.trim()) {
            errors.service_category = 'Informe a categoria.';
         }
         if (duration !== '') {
            const durationNum = parseInt(duration, 10);
            if (
               Number.isNaN(durationNum) ||
               durationNum < 0 ||
               String(durationNum) !== String(duration).trim()
            ) {
               errors.estimated_completion_service_time =
                  'Informe um inteiro de minutos ≥ 0 (ou deixe vazio).';
            }
         }
      }

      // ─── Validação da comissão (só se o financeiro edita) ───
      let commissionChanged = false;
      if (canManageCommission && isEdit) {
         commissionChanged = commission.trim() !== originalCommission;
         if (commissionChanged) {
            const commissionNum = Number(commission);
            if (
               commission.trim() === '' ||
               Number.isNaN(commissionNum) ||
               commissionNum < 0
            ) {
               errors.commission_value_per_unit =
                  'Informe um valor ≥ 0,00.';
            }
         }
      }

      if (Object.keys(errors).length > 0) {
         setFieldErrors(errors);
         return;
      }

      // ─── Monta o payload por dono ───
      const out = {};
      if (canManage) {
         const catalogBody = {
            service_description: description.trim(),
            product_type: productType.trim(),
            service_category: serviceCategory.trim(),
            estimated_completion_service_time:
               duration === '' ? null : parseInt(duration, 10),
            is_active: isActive,
         };
         if (!isEdit) catalogBody.service_code = code.trim();
         out.catalogBody = catalogBody;
      }
      if (canManageCommission && isEdit && commissionChanged) {
         out.commission = Number(commission);
      }

      // Financeiro abriu a ficha mas não mexeu na comissão (e não é logística):
      // nada a salvar → só fecha.
      if (!out.catalogBody && out.commission === undefined) {
         onClose();
         return;
      }

      setFieldErrors({});
      setGeneralError(null);
      setIsSubmitting(true);
      try {
         await onSubmit(out);
         // Sucesso: o pai fecha o modal e recarrega a lista.
      } catch (err) {
         const mapped = mapErrors(err);
         setFieldErrors(mapped);
         const apiMessage = err?.response?.data?.message;
         if (err?.response?.status === 409) {
            setGeneralError(
               apiMessage ?? 'Já existe um serviço com esse código.'
            );
         } else if (Object.keys(mapped).length === 0) {
            setGeneralError(apiMessage ?? 'Erro ao salvar o serviço.');
         }
         setIsSubmitting(false);
      }
   };

   // Campos da logística ficam read-only para quem só edita comissão.
   const logisticsDisabled = isSubmitting || !canManage;

   let confirmLabel = isEdit ? 'Salvar alterações' : 'Criar serviço';
   if (isSubmitting) confirmLabel = 'Salvando…';

   return (
      <Modal
         isOpen
         onClose={isSubmitting ? () => {} : onClose}
         title={isEdit ? 'Editar serviço' : 'Novo serviço'}
         maxWidth="520px"
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
               >
                  {confirmLabel}
               </PrimaryButton>
            </>
         }
      >
         <Body>
            {generalError && <WarnBox>{generalError}</WarnBox>}

            <Field>
               <label htmlFor="svc-code">Código</label>
               <input
                  id="svc-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  disabled={isEdit || logisticsDisabled}
                  placeholder="Ex.: TEFN"
                  maxLength={20}
               />
               {isEdit ? (
                  <Hint>O código é imutável após a criação.</Hint>
               ) : (
                  <Hint>MAIÚSCULAS, dígitos e _ , começando por letra.</Hint>
               )}
               {fieldErrors.service_code && (
                  <FieldError>{fieldErrors.service_code}</FieldError>
               )}
            </Field>

            <Field>
               <label htmlFor="svc-desc">Descrição</label>
               <input
                  id="svc-desc"
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={logisticsDisabled}
                  placeholder="Ex.: TROCA ELEM. FILT. - NORMAL"
                  maxLength={150}
               />
               {fieldErrors.service_description && (
                  <FieldError>{fieldErrors.service_description}</FieldError>
               )}
            </Field>

            <TwoCol>
               <Field>
                  <label htmlFor="svc-product">Tipo de produto</label>
                  <input
                     id="svc-product"
                     type="text"
                     value={productType}
                     onChange={(e) => setProductType(e.target.value)}
                     disabled={logisticsDisabled}
                     placeholder="Ex.: TROCA DE FILTRO"
                     maxLength={120}
                  />
                  {fieldErrors.product_type && (
                     <FieldError>{fieldErrors.product_type}</FieldError>
                  )}
               </Field>

               <Field>
                  <label htmlFor="svc-category">Categoria</label>
                  <input
                     id="svc-category"
                     type="text"
                     value={serviceCategory}
                     onChange={(e) => setServiceCategory(e.target.value)}
                     disabled={logisticsDisabled}
                     placeholder="Ex.: INSTALAÇÃO ATE 4"
                     maxLength={120}
                  />
                  {fieldErrors.service_category && (
                     <FieldError>{fieldErrors.service_category}</FieldError>
                  )}
               </Field>
            </TwoCol>

            <Field>
               <label htmlFor="svc-duration">
                  Tempo de execução (min/unidade)
               </label>
               <input
                  id="svc-duration"
                  type="number"
                  min="0"
                  step="1"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  disabled={logisticsDisabled}
                  placeholder="Ex.: 30"
               />
               <Hint>
                  Vazio = não parametrizado (duração calculada 0 até preencher).
               </Hint>
               {fieldErrors.estimated_completion_service_time && (
                  <FieldError>
                     {fieldErrors.estimated_completion_service_time}
                  </FieldError>
               )}
            </Field>

            {/* Comissão — dono FINANCEIRO. Em edição: habilitada só p/ quem tem
                finance:commission:manage (demais veem read-only). Na criação:
                nasce 0,00, não editável aqui. */}
            {isEdit ? (
               <Field>
                  <label htmlFor="svc-commission">
                     Comissão por unidade (R$)
                  </label>
                  <input
                     id="svc-commission"
                     type="number"
                     min="0"
                     step="0.01"
                     value={commission}
                     onChange={(e) => setCommission(e.target.value)}
                     disabled={isSubmitting || !canManageCommission}
                     placeholder="0,00"
                  />
                  {!canManageCommission && (
                     <Hint>
                        Editável apenas pelo financeiro (finance:commission:manage).
                     </Hint>
                  )}
                  {fieldErrors.commission_value_per_unit && (
                     <FieldError>
                        {fieldErrors.commission_value_per_unit}
                     </FieldError>
                  )}
               </Field>
            ) : (
               <Hint>
                  A comissão nasce R$ 0,00 e é definida pelo financeiro após a
                  criação.
               </Hint>
            )}

            {canManage && (
               <CheckboxRow>
                  <input
                     type="checkbox"
                     checked={isActive}
                     onChange={(e) => setIsActive(e.target.checked)}
                     disabled={isSubmitting}
                  />
                  Ativo (disponível para seleção nos itens)
               </CheckboxRow>
            )}
         </Body>
      </Modal>
   );
}

ServiceTypeFormModal.propTypes = {
   mode: PropTypes.oneOf(['create', 'edit']).isRequired,
   serviceType: PropTypes.object,
   canManage: PropTypes.bool,
   canManageCommission: PropTypes.bool,
   onClose: PropTypes.func.isRequired,
   onSubmit: PropTypes.func.isRequired,
};
